import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
  CircularProgress,
  // Stack,
  Divider,
  Badge,
} from '@mui/material';
import { GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { ReusableDataGrid, CustomDrawer, ActionsMenu, type ActionMenuItem } from '@/components/common';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { RootState, AppDispatch } from '@/store/types';
import { useAuth } from '@/hooks';
import {
  fetchCompanies,
  createCompany,
  updateCompany,
  deleteCompany,
} from '@/store/slices/companySlice';
import { CompanyFormData } from '@/services/company/companyService';

const validationSchema = Yup.object({
  name: Yup.string().required('Company name is required').min(2, 'Name must be at least 2 characters'),
  gst: Yup.string().required('GST number is required').matches(/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/, 'Invalid GST format'),
  address1: Yup.string().required('Address line 1 is required'),
  address2: Yup.string(),
  city: Yup.string().required('City is required'),
  state: Yup.string().required('State is required'),
  pincode: Yup.string().required('Pincode is required').matches(/^\d{6}$/, 'Pincode must be 6 digits'),
  mobile: Yup.string().required('Mobile number is required').matches(/^\+?[1-9]\d{9,14}$/, 'Invalid mobile number'),
  email: Yup.string().required('Email is required').email('Invalid email format'),
  isActive: Yup.boolean().required('Status is required'),
  remarks: Yup.string(),
});

const initialFormData: CompanyFormData = {
  name: '',
  gst: '',
  address1: '',
  address2: '',
  city: '',
  state: '',
  pincode: '',
  mobile: '',
  email: '',
  isActive: true,
  remarks: '',
};

export const ManageCompany: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useAuth();
  const { companies, companiesLoading, isLoading, pagination } = useSelector(
    (state: RootState) => state.company
  );

  const [openDrawer, setOpenDrawer] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isActiveFilter, setIsActiveFilter] = useState<boolean | ''>('');
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });

  const formik = useFormik({
    initialValues: initialFormData,
    validationSchema,
    onSubmit: async (values) => {
      try {
        const dataWithUserId = {
          ...values,
          userId: user?._id || user?.id
        };
        
        if (editingId) {
          await dispatch(updateCompany({
            id: editingId,
            data: dataWithUserId
          }));
        } else {
          await dispatch(createCompany(dataWithUserId));
        }
        handleCloseDrawer();
        loadData();
      } catch (error) {
        console.error('Error saving company:', error);
      }
    },
  });

  const loadData = useCallback(() => {
    dispatch(fetchCompanies({ 
      page: paginationModel.page + 1,
      limit: paginationModel.pageSize,
      search: searchTerm || undefined,
      isActive: isActiveFilter !== '' ? isActiveFilter : undefined
    }));
  }, [dispatch, paginationModel, searchTerm, isActiveFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleOpenDrawer = (company?: any) => {
    if (company) {
      formik.setValues({
        name: company.name,
        gst: company.gst,
        address1: company.address1,
        address2: company.address2 || '',
        city: company.city,
        state: company.state,
        pincode: company.pincode,
        mobile: company.mobile,
        email: company.email,
        isActive: company.isActive,
        remarks: company.remarks || '',
      });
      setEditingId(company._id);
    } else {
      formik.resetForm();
      setEditingId(null);
    }
    setOpenDrawer(true);
  };

  const handleCloseDrawer = () => {
    setOpenDrawer(false);
    formik.resetForm();
    setEditingId(null);
  };

  const handleDelete = async (id: string) => {
    try {
      await dispatch(deleteCompany(id));
      setDeleteConfirmId(null);
      loadData();
    } catch (error) {
      console.error('Error deleting company:', error);
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setIsActiveFilter('');
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (searchTerm) count++;
    if (isActiveFilter !== '') count++;
    return count;
  };

  const activeFilterCount = getActiveFilterCount();

  const getStatusColor = (isActive: boolean) => {
    return isActive ? 'success' : 'default';
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Company Name',
      flex: 1,
      minWidth: 180,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="medium">
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'gst',
      headerName: 'GST Number',
      flex: 0.8,
      minWidth: 140,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="bold" color="primary">
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'mobile',
      headerName: 'Mobile',
      flex: 0.8,
      minWidth: 130,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'city',
      headerName: 'City',
      flex: 0.7,
      minWidth: 100,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'state',
      headerName: 'State',
      flex: 0.7,
      minWidth: 100,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'isActive',
      headerName: 'Status',
      flex: 0.6,
      minWidth: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Active' : 'Inactive'}
          color={getStatusColor(params.value) as any}
          size="small"
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const actions: ActionMenuItem[] = [
          {
            id: 'edit',
            title: 'Edit',
            onClick: () => handleOpenDrawer(params.row),
            disabled: isLoading,
          },
          {
            id: 'delete',
            title: 'Delete',
            onClick: () => setDeleteConfirmId(params.row._id),
            disabled: isLoading,
          },
        ];

        return (
          <ActionsMenu
            actions={actions}
            disabled={isLoading}
          />
        );
      },
    },
  ];

  return (
    <Box>
      {/* Header */}
      <Box mb={3}>
        <Box display="flex" alignItems="center" mb={2}>
          <Typography variant="h5" component="h5" flexGrow={1}>
            Manage Companies
          </Typography>
        </Box>
        <Divider />
      </Box>

      {/* Filter and Action Buttons */}
      <Grid container spacing={2} alignItems="center" justifyContent="flex-end" sx={{ mb: 3 }}>
        <Grid size="auto">
          <Badge badgeContent={activeFilterCount} color="primary">
            <Button
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={() => setFilterDrawerOpen(true)}
            >
              Filters
            </Button>
          </Badge>
        </Grid>
        <Grid size="auto">
          <Button
            variant="contained"
            onClick={() => handleOpenDrawer()}
            disabled={isLoading}
          >
            Add Company
          </Button>
        </Grid>
      </Grid>

      <ReusableDataGrid
        rows={companies || []}
        columns={columns}
        getRowId={(row) => row?._id}
        loading={companiesLoading}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        paginationData={pagination ? { totalRecords: pagination.totalItems } : undefined}
        noRowsMessage="No companies found"
        noRowsActionLabel="Create Company"
        onNoRowsAction={() => handleOpenDrawer()}
      />

      {/* Create/Edit Drawer */}
      <CustomDrawer
        open={openDrawer}
        onClose={handleCloseDrawer}
        title={editingId ? 'Edit Company' : 'Create Company'}
        subtitle={editingId ? 'Update company details' : 'Add a new company to the system'}
                 width={'100%'}
        disableBackdropClick={true}
      >
        {/* <form onSubmit={formik.handleSubmit}> */}
          <Box sx={{ py: 3 }}>
            <Grid container spacing={3}>
              <Grid size={{xs: 12}}>
                <TextField
                  fullWidth
                  label="Company Name"
                  name="name"
                  required
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                  placeholder="e.g., Tech Solutions Pvt Ltd"
                />
              </Grid>
              <Grid size={{xs: 12}}>
                <TextField
                  fullWidth
                  label="GST Number"
                  required
                  name="gst"
                  value={formik.values.gst}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.gst && Boolean(formik.errors.gst)}
                  helperText={formik.touched.gst && formik.errors.gst}
                  placeholder="e.g., 22AAAAA0000A1Z5"
                />
              </Grid>
              <Grid size={{xs: 12}}>
                <TextField
                  fullWidth
                  label="Address Line 1"
                  name="address1"
                  required
                  value={formik.values.address1}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.address1 && Boolean(formik.errors.address1)}
                  helperText={formik.touched.address1 && formik.errors.address1}
                  placeholder="e.g., 123 Business Park, Sector 5"
                />
              </Grid>
              <Grid size={{xs: 12}}>
                <TextField
                  fullWidth
                  label="Address Line 2 (Optional)"
                  name="address2"
                  value={formik.values.address2}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.address2 && Boolean(formik.errors.address2)}
                  helperText={formik.touched.address2 && formik.errors.address2}
                  placeholder="e.g., Near Metro Station"
                />
              </Grid>
              <Grid size={{xs: 12, sm: 6}}>
                <TextField
                  fullWidth
                  label="City"
                  name="city"
                  required
                  value={formik.values.city}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.city && Boolean(formik.errors.city)}
                  helperText={formik.touched.city && formik.errors.city}
                  placeholder="e.g., Mumbai"
                />
              </Grid>
              <Grid size={{xs: 12, sm: 6}}>
                <TextField
                  fullWidth
                  label="State"
                  name="state"
                  required
                  value={formik.values.state}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.state && Boolean(formik.errors.state)}
                  helperText={formik.touched.state && formik.errors.state}
                  placeholder="e.g., Maharashtra"
                />
              </Grid>
              <Grid size={{xs: 12, sm: 6}}>
                <TextField
                  fullWidth
                  label="Pincode"
                  name="pincode"
                  required
                  value={formik.values.pincode}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.pincode && Boolean(formik.errors.pincode)}
                  helperText={formik.touched.pincode && formik.errors.pincode}
                  placeholder="e.g., 400001"
                />
              </Grid>
              <Grid size={{xs: 12, sm: 6}}>
                <TextField
                  fullWidth
                  label="Mobile Number"
                  name="mobile"
                  required
                  value={formik.values.mobile}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.mobile && Boolean(formik.errors.mobile)}
                  helperText={formik.touched.mobile && formik.errors.mobile}
                  placeholder="e.g., +919876543210"
                />
              </Grid>
              <Grid size={{xs: 12}}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  required
                  type="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                  placeholder="e.g., contact@techsolutions.com"
                />
              </Grid>
              <Grid size={{xs: 12}}>
                <TextField
                  fullWidth
                  select
                  name="isActive"
                  label="Status"
                  required
                  value={formik.values.isActive}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.isActive && Boolean(formik.errors.isActive)}
                  helperText={formik.touched.isActive && formik.errors.isActive}
                  variant="outlined"
                  slotProps={{
                    select: {
                      native: true,
                    },
                  }}
                >
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </TextField>
              </Grid>
              <Grid size={{xs: 12}}>
                <TextField
                  fullWidth
                  label="Remarks (Optional)"
                  name="remarks"
                  multiline
                  rows={3}
                  value={formik.values.remarks}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.remarks && Boolean(formik.errors.remarks)}
                  helperText={formik.touched.remarks && formik.errors.remarks}
                  placeholder="Additional notes about the company..."
                />
              </Grid>
            </Grid>
          </Box>
          <Box sx={{ p: 3, pt: 0, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button onClick={handleCloseDrawer} disabled={isLoading}>
              Cancel
            </Button>
            <Button
              // type="submit"
              onClick={(e:any) => formik.handleSubmit(e)}
              variant="contained"
              disabled={isLoading}
            >
              {isLoading ? <CircularProgress size={20} /> : editingId ? 'Update' : 'Create'}
            </Button>
          </Box>
        {/* </form> */}
      </CustomDrawer>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirmId} onClose={() => setDeleteConfirmId(null)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this company? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmId(null)}>Cancel</Button>
          <Button
            onClick={() => deleteConfirmId && handleDelete(deleteConfirmId)}
            color="error"
            variant="contained"
            disabled={isLoading}
          >
            {isLoading ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Filter Drawer */}
      <CustomDrawer
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        title="Filter Companies"
                width={'100%'}

        anchor="right"
        actions={
          <Button
            variant="outlined"
            color="secondary"
            onClick={clearFilters}
            disabled={activeFilterCount === 0}
            startIcon={<FilterIcon />}
            fullWidth
          >
            Clear Filters
          </Button>
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Search companies"
            variant="outlined"
            size="small"
            fullWidth
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by name, GST, email..."
            slotProps={{
              input: {
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              },
            }}
          />
          
          <TextField
            select
            label="Filter by Status"
            variant="outlined"
            size="small"
            fullWidth
            value={isActiveFilter}
            onChange={(e) => setIsActiveFilter(e.target.value === '' ? '' : e.target.value === 'true')}
            slotProps={{
              select: {
                native: true,
              },
            }}
          >
            <option value="">All Status</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </TextField>
          
          {activeFilterCount > 0 && (
            <Box mt={2}>
              <Typography variant="body2" color="text.secondary">
                {activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''} applied
              </Typography>
            </Box>
          )}
        </Box>
      </CustomDrawer>
    </Box>
  );
};